import { useMemo } from "react";
import { GridColDef } from "@mui/x-data-grid-pro";
import { Box } from "@mui/system";
import { t } from "i18next";
import { Autocomplete, TextField, Typography } from "@mui/material";
import CustomTooltip from "src/components/visibility/components/CustomTooltip";
import Iconify from "src/components/iconify";
import { formatNumber } from "src/utils/format-number";
import GreenCheckSwitch from "../components/GreenCheckSwitch";
import ActivationCell from "../data-grid-keyword-other/components/ActivationCell";
import { formatCurrency } from "src/utils/format-currency";
import PercentageChange from "../components/PercentageChange";
import { formatKeywordByMatchType } from "./formatKeywordByMatchType";

export const useKeywordOtherColumns = ({
    workspace,
    lang,
    handleToggle,
    selectedCompareDate,
    setConfirmDisable,
    setModalCreateStrategy,
}: any): GridColDef[] => {

    return useMemo(
        () => [
            {
                field: "keyword",
                headerName: t("dashboard.global.keyword"),
                disableColumnMenu: true,
                flex: 1,
                minWidth: 285,
                headerClassName: "keyword-header",
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography
                            fontWeight={600}
                            color="rgba(99, 115, 129, 1)"
                            fontSize={14}
                        >
                            {t("dashboard.global.keyword")}
                        </Typography>

                        <CustomTooltip title={"Ad clicks"}
                            placement="top"
                            arrow>
                            <Iconify icon="proicons:cursor-click" sx={{ cursor: 'help' }} width={20} height={20} />
                        </CustomTooltip>
                    </Box>
                ),

                renderCell: (params) => {
                    const formattedKeyword = formatKeywordByMatchType(
                        params.row.keyword,
                        params.row.match_type
                    );

                    return (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                                height: "60px",
                                padding: "0px 12px 0px 8px",
                                gap: 3,
                            }}
                        >
                            <CustomTooltip title={params.row.keyword} arrow>
                                <Typography
                                    fontSize={14}
                                    color="rgba(51, 60, 71, 1)"
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        cursor: "help",
                                    }}
                                >
                                    {formattedKeyword}
                                </Typography>
                            </CustomTooltip>

                            <Typography
                                fontSize={14}
                                color="rgba(99, 115, 129, 1)"
                                sx={{
                                    marginLeft: "auto",
                                    textAlign: "right",
                                }}
                            >
                                {formatNumber(params.row.clicks)}
                            </Typography>
                        </Box>
                    );
                },
            },
            {
                field: "keyword_id",
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Keyword ID
                        </Typography>
                    </Box>
                ),
                headerName: "Keyword ID",
                minWidth: 200,
                type: "number",
                sortable: false,
                disableColumnMenu: true,
            },
            {
                field: "match_type",
                headerName: "Match Type",
                minWidth: 140,
                sortable: true,
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Match Type
                        </Typography>
                    </Box>
                ),
                type: "singleSelect",
                valueOptions: [
                    { value: "BROAD", label: "Broad" },
                    { value: "PHRASE", label: "Phrase" },
                ],

                valueGetter: (params: any) => (params || "").toUpperCase(),

                renderCell: (params) => {
                    const map: any = {
                        BROAD: "Broad",
                        PHRASE: "Phrase",
                    };

                    return map[params.value] || params.value;
                },
            },
            {
                field: "platform",
                headerName: "Platform",
                minWidth: 140,
                sortable: true,
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Platform
                        </Typography>
                    </Box>
                ),
                type: "singleSelect",
                valueOptions: [
                    { value: "GOOGLE", label: "Google Ads" },
                    { value: "BING", label: "Bing Ads" },
                ],

                valueGetter: (params: any) => (params || "").toUpperCase(),

                renderCell: (params) => {
                    const map: any = {
                        GOOGLE: "Google Ads",
                        BING: "Bing Ads",
                    };

                    return map[params.value] || params.value;
                },
            },
            // Campaigns
            {
                field: "campaign_names",
                sortable: true,
                disableColumnMenu: true,
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Campaign
                        </Typography>
                    </Box>
                ),
                headerName: "Campaign",
                flex: 1,
                minWidth: 260,
                renderCell: ({ value }) => {
                    return (
                        <CustomTooltip
                            title={value?.join(", ")}
                            placement="top"
                            arrow
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        px: "12px",
                                        py: "8px",
                                        borderRadius: "8px",
                                        backgroundColor: "#FCFCFD",
                                        boxShadow: "1px 3px 8px 0 rgba(0, 0, 0, 0.08)",
                                    },
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                    height: "60px",
                                    px: "12px",
                                    minWidth: 0,
                                    overflow: "hidden",
                                }}
                            >
                                <Typography
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        fontSize: '14px',
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        color: "rgba(51, 60, 71, 1)",
                                        cursor: 'help'
                                    }}
                                >
                                    {(value || []).join(", ")}
                                </Typography>
                            </Box>
                        </CustomTooltip>
                    );
                }
            },
            {
                field: "campaign_ids",
                type: 'number',
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Campaign ID
                        </Typography>
                    </Box>
                ),
                disableColumnMenu: true,
                headerName: "Campaign ID",
                flex: 1,
                sortable: false
            },
            {
                field: "ad_group_names",
                disableColumnMenu: true,
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Ad Group
                        </Typography>
                    </Box>
                ),
                headerName: "Ad Group Name",
                minWidth: 200,
                sortable: true
            },
            {
                field: "ad_group_ids",
                type: 'number',
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Ad Group ID
                        </Typography>
                    </Box>
                ),
                disableColumnMenu: true,
                headerName: "Ad Group ID",
                flex: 1,
                sortable: false
            },
            // Scraping Status
            {
                field: "scraping_status",
                disableColumnMenu: true,
                sortable: false,
                headerAlign: 'left',
                headerName: "Scraping Status",
                filterable: true,
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            width: "100%",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Scraping Status
                        </Typography>
                    </Box>
                ),
                type: 'boolean',
                minWidth: 150,
                renderCell: ({ row }) => {
                    const activeDetail = row.scraping_details?.find((s: any) => s.status === true);
                    const freq = activeDetail?.frequency;

                    const switchComponent = (
                        <Box
                            px="12px"
                            sx={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <GreenCheckSwitch
                                checked={!!activeDetail}
                                onChange={() => handleToggle(row, !!activeDetail)}
                            />
                        </Box>
                    );

                    if (activeDetail) {
                        return (
                            <CustomTooltip
                                title={freq ? `Frequency: ${freq}` : "No frequency"}
                                placement="top"
                                arrow
                            >
                                {switchComponent}
                            </CustomTooltip>
                        );
                    }

                    return switchComponent;
                },
            },
            {
                field: "mode",
                headerName: "Mode",
                disableColumnMenu: true,
                filterable: true,
                minWidth: 120,
                maxWidth: 120,
                width: 120,
                sortable: false,
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                            flexDirection: "column"
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Mode
                        </Typography>
                    </Box>
                ),
                editable: false,
                filterOperators: [
                    {
                        label: "is any of",
                        value: "isAnyOf",
                        getApplyFilterFn: () => null,

                        InputComponent: (props) => {
                            const { item, applyValue } = props;

                            return (
                                <Autocomplete
                                    sx={{ mt: 2.5 }}
                                    multiple
                                    options={[
                                        "KO Bidding Manual"
                                    ]}
                                    value={item.value || []}
                                    onChange={(event, newValue) => {
                                        applyValue({
                                            ...item,
                                            value: newValue,
                                        });
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="standard"
                                            placeholder="Select modes"
                                        />
                                    )}
                                    size="small"
                                />
                            );
                        },
                    },
                ],
                renderCell: (params) => (
                    <ActivationCell
                        params={params}
                        setConfirmDisable={setConfirmDisable}
                        setModalCreateStrategy={setModalCreateStrategy}
                    />
                )
            },
            {
                disableColumnMenu: true,
                field: "max_cpc",
                filterable: false,
                type: "string",
                sortable: false,
                headerAlign: 'left',
                headerName: "Max CPC",

                renderHeader: () => (
                    <Typography
                        fontWeight={600}
                        fontSize={14}
                        color="rgba(99, 115, 129, 1)"
                        sx={{
                            whiteSpace: "normal",
                            lineHeight: 1.2,
                        }}
                    >
                        Set Max CPC
                    </Typography>
                ),

                minWidth: 140,
                editable: true,
                renderEditCell: (params) => {
                    return (
                        <Box
                            display="flex"
                            width="100%"
                            height="100%"
                            alignItems="center"
                            justifyContent="flex-end"
                            px={1}
                        >
                            <TextField
                                autoFocus
                                value={params.value ?? ""}
                                onChange={(event) => {
                                    let value = event.target.value;

                                    value = value.replace(",", ".");

                                    params.api.setEditCellValue({
                                        id: params.id,
                                        field: params.field,
                                        value,
                                    });
                                }}
                                variant="outlined"
                                size="small"
                                fullWidth
                                inputProps={{
                                    style: { textAlign: "right" },
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        textAlign: "right",
                                        "& fieldset": {
                                            borderRadius: "6px",
                                        },
                                        "&:hover fieldset": {
                                            border: "1px solid var(--color-azure-58, #338AF3)",
                                            background: "rgba(255, 255, 255, 0.01)",
                                            boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.20)",
                                        },
                                        "&.Mui-focused fieldset": {
                                            border: "1px solid var(--color-azure-58, #338AF3)",
                                            boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.20)",
                                        },
                                    },
                                    "& input": {
                                        textAlign: "right",
                                        cursor: "pointer",
                                    },
                                }}
                            />
                        </Box>
                    );
                },

                renderCell: (params: any) => {
                    const activations = params.row.activations_details || [];

                    const koManual = activations.find(
                        (d: any) => d.status === true && d.mode === "KO Bidding Manual"
                    );

                    const koAuto = activations.find(
                        (d: any) => d.status === true && d.mode?.startsWith("KO Bidding Auto")
                    );

                    const value =
                        params.row.max_cpc !== undefined
                            ? params.row.max_cpc
                            : koManual?.max_cpc;

                    const isAuto = !koManual && Boolean(koAuto);

                    const isActive = Boolean(koManual);

                    return (
                        <Box
                            sx={{
                                display: "flex",
                                width: "100%",
                                height: 60,
                                alignItems: "center",
                                boxSizing: "border-box",
                            }}
                        >
                            {isActive ? (
                                isAuto ? (
                                    <span style={{ opacity: 0.5, width: 52, textAlign: "right", display: "inline-block" }}>
                                        {params?.row?.max_cpc}
                                    </span>
                                ) : (
                                    <Box
                                        onClick={() => {
                                            params.api.startCellEditMode({
                                                id: params.id,
                                                field: params.field,
                                            });
                                        }}
                                        sx={{
                                            width: "100%",
                                            height: 36,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-end",
                                            px: "10px",
                                            borderRadius: "6px",
                                            border: "1px solid rgba(145, 158, 171, 0.20)",
                                            background: "rgba(255, 255, 255, 0.01)",
                                            fontSize: "0.875rem",
                                            lineHeight: 1,
                                            whiteSpace: "nowrap",
                                            boxSizing: "border-box",
                                            cursor: "pointer",
                                            transition: "all 0.15s ease",
                                            "&:hover": {
                                                border: "1px solid var(--color-azure-58, #338AF3)",
                                                boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.35)",
                                            },
                                        }}
                                    >
                                        {formatCurrency(value, workspace?.currency, lang, true, 2) ?? ""}
                                    </Box>
                                )
                            ) : (
                                <Typography
                                    sx={{
                                        width: '90%',
                                        textAlign: "right",
                                        fontFamily: '"Public Sans", sans-serif',
                                        fontSize: "14px",
                                        fontWeight: 400,
                                        lineHeight: "14px",
                                        color: "#adb1b5",
                                    }}
                                >
                                    {formatCurrency(params.row.max_cpc_3, workspace?.currency, lang, true, 2) ?? ""}
                                </Typography>
                            )}
                        </Box>
                    );
                },
            },
            {
                field: "max_cpc_2",
                sortable: true,
                headerAlign: "left",
                disableColumnMenu: true,
                filterable: true,
                renderHeader: () => (
                    <Typography
                        fontWeight={600}
                        fontSize={14}
                        color="rgba(99, 115, 129, 1)"
                        sx={{
                            whiteSpace: "normal",
                            lineHeight: 1.2,
                        }}
                    >
                        Max CPC Evol
                    </Typography>
                ),
                headerName: "Max CPC Evol",
                renderCell: ({ row }) => {
                    const currentMaxCpc = row.max_cpc_2 ?? 0;
                    const compareMaxCpc = row?.max_cpc_compare ?? 0;

                    let percentageChange = null;

                    if (compareMaxCpc > 0) {
                        percentageChange =
                            ((currentMaxCpc - compareMaxCpc) / compareMaxCpc) * 100;
                    }

                    return (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                height: 60,
                                px: "12px",
                                boxSizing: "border-box",
                                gap: "8px",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    color: "#333C47",
                                    whiteSpace: "nowrap",
                                    textAlign: "right",
                                }}
                            >
                                {formatCurrency(currentMaxCpc, workspace?.currency, lang, true, 2) ?? ""}
                            </Typography>

                            <Box
                                sx={{
                                    width: 50,
                                    textAlign: "right",
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                <PercentageChange value={percentageChange} inverse />
                            </Box>
                        </Box>
                    );
                },
                minWidth: 144,
                type: "number",
            },
            {
                field: "cost",
                disableColumnMenu: true,
                sortable: true,
                headerAlign: "left",
                filterable: true,

                renderHeader: () => (
                    <Typography
                        fontWeight={600}
                        color="rgba(99, 115, 129, 1)"
                        fontSize={14}
                    >
                        Ad Cost
                    </Typography>
                ),

                headerName: "Ad Cost",

                renderCell: ({ row }) => {
                    const currentCost = row.cost ?? 0;
                    const compareCost = row?.cost_compare ?? 0;

                    let percentageChange = null;

                    if (compareCost > 0) {
                        percentageChange =
                            ((currentCost - compareCost) / compareCost) * 100;
                    }

                    return (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                height: 60,
                                px: "22px",
                                boxSizing: "border-box",
                                gap: "8px",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    color: "#333C47",
                                    whiteSpace: "nowrap",
                                    textAlign: "right",
                                }}
                            >
                                {formatCurrency(
                                    currentCost,
                                    workspace?.currency,
                                    lang,
                                    true,
                                    0
                                )}
                            </Typography>

                            <Box
                                sx={{
                                    width: 50,
                                    textAlign: "right",
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                }}
                            >
                                <PercentageChange value={percentageChange} inverse />
                            </Box>
                        </Box>
                    );
                },
                minWidth: 184,
                type: "number",
            },
            // AD CLICKS
            {
                field: "clicks",
                disableColumnMenu: true,
                sortable: true,
                headerAlign: 'left',
                filterable: true,
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Ad Clicks
                        </Typography>
                    </Box>
                ),
                headerName: "Ad Clicks",
                renderCell: ({ row }) => {
                    const currentClicks = row.clicks ?? 0;
                    const compareClicks = row?.clicks_compare ?? 0;

                    let percentageChange: number | null = null;

                    if (compareClicks > 0) {
                        percentageChange = ((currentClicks - compareClicks) / compareClicks) * 100;
                    }

                    return (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                height: 60,
                                px: "22px",
                                boxSizing: "border-box",
                                gap: "8px",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    color: "#333C47",
                                    whiteSpace: "nowrap",
                                    textAlign: "right",
                                }}
                            >
                                {formatNumber(currentClicks)}
                            </Typography>
                            <Box
                                sx={{
                                    width: 50,
                                    textAlign: "right",
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                }}
                            >
                                <PercentageChange value={percentageChange} />
                            </Box>
                        </Box>
                    );
                },
                minWidth: 184,
                type: "number"
            },
            // AVG. CPC
            {
                field: "avg_cpc",
                disableColumnMenu: true,
                sortable: true,
                headerAlign: "left",
                filterable: true,

                renderHeader: () => (
                    <Typography
                        fontWeight={600}
                        color="rgba(99, 115, 129, 1)"
                        fontSize={14}
                    >
                        Avg. CPC
                    </Typography>
                ),

                headerName: "Avg. CPC",

                renderCell: ({ row }) => {
                    const currentCpc = row.avg_cpc ?? 0;
                    const compareCpc = row?.avg_cpc_compare ?? 0;

                    let percentageChange = null;

                    if (compareCpc > 0) {
                        percentageChange =
                            ((currentCpc - compareCpc) / compareCpc) * 100;
                    }

                    return (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                height: 60,
                                px: "12px",
                                boxSizing: "border-box",
                                gap: "8px",
                                width: "100%",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    color: "#333C47",
                                    whiteSpace: "nowrap",
                                    textAlign: "right",
                                }}
                            >
                                {formatCurrency(
                                    currentCpc,
                                    workspace?.currency,
                                    lang,
                                    true,
                                    2
                                )}
                            </Typography>

                            <Box
                                sx={{
                                    width: 50,
                                    textAlign: "right",
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                }}
                            >
                                <PercentageChange value={percentageChange} inverse />
                            </Box>
                        </Box>
                    );
                },

                minWidth: 184,
                type: "number",
            },
        ], [handleToggle, workspace?.currency, lang, selectedCompareDate]);
};